// TypeScript counterparts of the structured proof types defined in
// tabularium/pkg/model/model/proof.go. The shapes here mirror the JSON wire
// format produced by capability-sdk's capmodel/proof_model.go.
//
// Keep these in sync with proof.go. Renderer code dispatches on
// ProofElement.type and reads the matching variant pointer.

export const PROOF_FORMAT_V1 = 'elements/v1';

export type ProofSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'info'
  | 'success';

export type ProofElementKind =
  | 'heading'
  | 'paragraph'
  | 'group'
  | 'key_value'
  | 'table'
  | 'list'
  | 'code_block'
  | 'code_snippet'
  | 'http_exchange'
  | 'banner'
  | 'metric'
  | 'reference'
  | 'image'
  | 'timeline';

export interface ProofHeading {
  level: number;
  text: string;
}

export interface ProofParagraph {
  text: string;
  markdown?: boolean;
}

export interface ProofGroup {
  title?: string;
  elements: ProofElement[];
}

export interface ProofKeyValueRow {
  key: string;
  value: string;
  copyable?: boolean;
  href?: string;
  severity?: ProofSeverity;
}

export interface ProofKeyValue {
  rows: ProofKeyValueRow[];
}

export interface ProofTableColumn {
  key: string;
  label: string;
}

export interface ProofTable {
  columns: ProofTableColumn[];
  rows: Record<string, string>[];
}

export type ProofListStyle = 'bullet' | 'numbered' | 'plain';

export interface ProofListItem {
  label?: string;
  sublabel?: string;
  value?: string;
  copyable?: boolean;
  href?: string;
}

export interface ProofList {
  style?: ProofListStyle;
  items: ProofListItem[];
}

export interface ProofCodeBlock {
  language?: string;
  content: string;
}

export type ProofCodeHighlightKind = 'match' | 'context';

export interface ProofCodeHighlight {
  start_line: number;
  end_line?: number;
  kind?: ProofCodeHighlightKind;
}

export interface ProofCodeSnippet {
  file_path?: string;
  language?: string;
  start_line?: number;
  content: string;
  highlights?: ProofCodeHighlight[];
}

export interface ProofHttpHeader {
  name: string;
  value: string;
}

export interface ProofHttpMessage {
  method?: string;
  url?: string;
  status_code?: number;
  headers?: ProofHttpHeader[];
  body?: string;
  encoding?: string;
  truncated?: boolean;
}

export interface ProofHttpExchange {
  request?: ProofHttpMessage;
  response?: ProofHttpMessage;
}

export interface ProofBanner {
  severity: ProofSeverity;
  title: string;
  body?: string;
}

export interface ProofMetric {
  label: string;
  value: string;
  unit?: string;
  subtext?: string;
  severity?: ProofSeverity;
}

export type ProofReferenceKind =
  | 'cve'
  | 'cwe'
  | 'mitre'
  | 'repo'
  | 'url'
  | 'doc';

export interface ProofReference {
  kind: ProofReferenceKind;
  id?: string;
  label?: string;
  url?: string;
}

export interface ProofImage {
  url?: string;
  data_uri?: string;
  alt_text?: string;
  caption?: string;
}

export interface ProofTimelineEntry {
  title: string;
  subtitle?: string;
  reference?: string;
  href?: string;
}

export interface ProofTimeline {
  entries: ProofTimelineEntry[];
}

export interface ProofElement {
  type: ProofElementKind;
  heading?: ProofHeading;
  paragraph?: ProofParagraph;
  group?: ProofGroup;
  key_value?: ProofKeyValue;
  table?: ProofTable;
  list?: ProofList;
  code_block?: ProofCodeBlock;
  code_snippet?: ProofCodeSnippet;
  http_exchange?: ProofHttpExchange;
  banner?: ProofBanner;
  metric?: ProofMetric;
  reference?: ProofReference;
  image?: ProofImage;
  timeline?: ProofTimeline;
}

export interface ProofSection {
  title?: string;
  elements: ProofElement[];
}

export interface Proof {
  format: string;
  sections: ProofSection[];
}

// isStructuredProof reports whether the given parsed JSON value is a v1
// structured proof. Used by the legacy POE.tsx renderer to decide whether
// to delegate to the generic renderer.
export function isStructuredProof(value: unknown): value is Proof {
  if (!value || typeof value !== 'object') return false;
  const v = value as { format?: unknown; sections?: unknown };
  return v.format === PROOF_FORMAT_V1 && Array.isArray(v.sections);
}
